import React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';

import UserIcon from 'src/assets/user-icon';
import UnionIcon from 'src/assets/union-icon';
import EmailIcon from 'src/assets/email-icon';
import { MoneyIcon } from 'src/assets/money-icon';
import { RibbonStar } from 'src/assets/ribbon-star';
import { ReferralIcon } from 'src/assets/referral-icon';
import { VerticalTabs, VerticalTab } from 'components/core';
import useTranslation from 'hooks/use-translation';
import { ARIA_ROLES } from 'shared/constants';
import { formatName } from 'shared/helpers/users';
import { TabLink } from 'src/components/core/tabs/tab-link';
import { RouteParam } from 'src/types';
import { Typography } from 'src/typography';
import { usePrepaidWalletBalance } from 'src/hooks/use-prepaid-wallet-balance';
import { ConsumerUser, AccountRoutes, AccountTab } from '../account.types';

type AccountSideNavBarProps = {
  user: ConsumerUser;
  accountRoutes: AccountRoutes;
  selectedTab: RouteParam;
  shouldDisplayDutchiePaySection: boolean;
  shouldDisplayLoyaltySection: boolean;
  shouldDisplayReferralsSection: boolean;
  shouldDisplayPrepaidWalletSection: boolean;
};

type FormattedNameReturn = {
  firstName: string;
  lastName: string;
};

function formatUserName(user: ConsumerUser): FormattedNameReturn {
  const formattedName = formatName(user.profile);
  const isLongName = (name: string): boolean => name.length > 18;
  if (typeof formattedName !== 'string') {
    throw new Error(`Error: type of user name must be of type string, got ${typeof formattedName} instead.`);
  }

  const truncateName = formattedName.length > 26;
  const [firstName, ...lastNameParts] = formattedName.split(' ');
  const lastName = lastNameParts.join(' ');

  if (truncateName) {
    return {
      firstName: isLongName(firstName) ? `${firstName.slice(0, 14)}...` : firstName,
      lastName: isLongName(lastName) ? `${lastName.slice(0, 14)}...` : lastName,
    };
  }

  return {
    firstName,
    lastName,
  };
}

const ANCHOR_COMPONENT = `a`;

export const AccountSideNavBar = observer(
  (props: AccountSideNavBarProps): JSX.Element => {
    const {
      user,
      accountRoutes,
      selectedTab,
      shouldDisplayDutchiePaySection,
      shouldDisplayLoyaltySection,
      shouldDisplayReferralsSection,
      shouldDisplayPrepaidWalletSection,
    } = props;
    const { account, preferences, orders, bankAccount, loyalty, referrals, wallet } = accountRoutes;

    const { t } = useTranslation();
    const { formattedBalance: walletBalance } = usePrepaidWalletBalance();
    const { firstName, lastName } = formatUserName(user);

    return (
      <SideNavBarContainer>
        <Header>
          <Typography.Heading size='small' tag='h2'>
            {firstName}
            <br />
            {lastName}
          </Typography.Heading>
        </Header>

        <StyledTabs data-testid='account-navigation-tabs' selectionFollowsFocus value={selectedTab}>
          {shouldDisplayPrepaidWalletSection && !!wallet && (
            <TabLink value={AccountTab.wallet} href={wallet}>
              <StyledTab
                id='account-tab-wallet'
                data-testid='account-tab-wallet'
                aria-controls='account-tab-panel-wallet'
                aria-selected={selectedTab === AccountTab.wallet}
                component={ANCHOR_COMPONENT}
                icon={MoneyIcon}
                label={t('accountPage.wallet', 'Wallet · {{balance}}', {
                  balance: walletBalance,
                })}
                role={ARIA_ROLES.TAB}
                value={AccountTab.wallet}
              />
            </TabLink>
          )}

          {shouldDisplayDutchiePaySection && !!bankAccount && (
            <TabLink value={AccountTab.bankAccount} href={bankAccount}>
              <StyledTab
                id='account-tab-bank-account'
                data-testid='account-tab-bank-account'
                aria-controls='account-tab-panel-bank-account'
                aria-selected={selectedTab === AccountTab.bankAccount}
                component={ANCHOR_COMPONENT}
                icon={MoneyIcon}
                label={t('accountPage.bankAccount', 'Bank Account')}
                role={ARIA_ROLES.TAB}
                value={AccountTab.bankAccount}
              />
            </TabLink>
          )}

          <TabLink value={AccountTab.account} href={account}>
            <StyledTab
              id='account-tab-my-account'
              data-testid='account-tab-my-account'
              aria-controls='account-tab-panel-my-account'
              aria-selected={selectedTab === AccountTab.account}
              component={ANCHOR_COMPONENT}
              icon={UserIcon}
              label={t('accountPage.accountDetails', 'Account Details')}
              role={ARIA_ROLES.TAB}
              value={AccountTab.account}
            />
          </TabLink>

          <TabLink value={AccountTab.orders} href={orders}>
            <StyledTab
              id='account-tab-orders'
              data-testid='account-tab-orders'
              aria-controls='account-tab-panel-orders'
              aria-selected={selectedTab === AccountTab.orders}
              icon={UnionIcon}
              label={t('accountPage.orders', 'Orders')}
              role={ARIA_ROLES.TAB}
              value={AccountTab.orders}
            />
          </TabLink>

          {shouldDisplayReferralsSection && !!referrals && (
            <TabLink value={AccountTab.referrals} href={referrals}>
              <StyledTab
                id='account-tab-referrals'
                data-testid='account-tab-referrals'
                aria-controls='account-tab-panel-referrals'
                aria-selected={selectedTab === AccountTab.referrals}
                icon={ReferralIcon}
                label={t('accountPage.referrals', 'Referrals')}
                role={ARIA_ROLES.TAB}
                value={AccountTab.referrals}
              />
            </TabLink>
          )}

          {shouldDisplayLoyaltySection && !!loyalty && (
            <TabLink value={AccountTab.loyalty} href={loyalty}>
              <StyledTab
                id='account-tab-loyalty'
                data-testid='account-tab-loyalty'
                aria-controls='account-tab-panel-loyalty'
                aria-selected={selectedTab === AccountTab.loyalty}
                icon={RibbonStar}
                label={t('accountPage.loyalty', 'Loyalty')}
                role={ARIA_ROLES.TAB}
                value={AccountTab.loyalty}
              />
            </TabLink>
          )}

          <TabLink value={AccountTab.preferences} href={preferences}>
            <StyledTab
              id='account-tab-preferences'
              data-testid='account-tab-preferences'
              aria-controls='account-tab-panel-preferences'
              aria-selected={selectedTab === AccountTab.preferences}
              component={ANCHOR_COMPONENT}
              icon={EmailIcon}
              label={t('accountPage.preferences', 'Preferences')}
              role={ARIA_ROLES.TAB}
              value={AccountTab.preferences}
            />
          </TabLink>
        </StyledTabs>
      </SideNavBarContainer>
    );
  }
);

const SideNavBarContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 50px;
  min-width: 279px;
`;

const Header = styled.div`
  padding-left: 73px;
  color: var(--dcm-color-text-primary);
`;

const StyledTabs = styled(VerticalTabs)`
  margin-top: 30px;
  color: var(--dcm-color-text-primary);
  &.MuiTabs-root {
    box-shadow: none !important;
  }
`;

const StyledTab = styled(VerticalTab)`
  .MuiTab-wrapper {
    margin-left: 39px;
    align-items: flex-start;
  }
  &.MuiTab-root {
    margin-bottom: 8px;
    margin-top: 8px;
  }
`;
